"use client";

/**
 * Compact language picker.
 *
 * Two visual variants:
 *   - "topbar": a small dropdown for the app shell topbar.
 *   - "inline": a flat segmented control for the intake widget header and
 *     the public landing nav, where the bg is darker and a dropdown would
 *     read as chrome.
 */

import { Globe } from "lucide-react";
import { Select } from "@/components/ui";
import { useLocale, SUPPORTED_LOCALES, LOCALE_LABEL, type SupportedLocale } from "@/i18n";
import { cn } from "@/lib/cn";

export function LanguageSwitcher({
  variant = "topbar",
  className = "",
}: {
  variant?: "topbar" | "inline";
  className?: string;
}) {
  const { locale, setLocale } = useLocale();

  if (variant === "inline") {
    return (
      <div className={cn("inline-flex items-center gap-1 rounded-md border border-line-subtle bg-bg-base p-0.5", className)}>
        {SUPPORTED_LOCALES.map((loc) => (
          <button
            key={loc}
            type="button"
            onClick={() => setLocale(loc)}
            aria-pressed={loc === locale}
            className={cn(
              "px-2 py-0.5 text-[11px] rounded uppercase tracking-wide font-medium transition-colors",
              loc === locale
                ? "bg-bg-elev text-fg"
                : "text-fg-muted hover:text-fg",
            )}
          >
            {loc}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className={cn("inline-flex items-center gap-1.5 text-fg-muted", className)}>
      <Globe size={13} className="pointer-events-none shrink-0" />
      <Select
        size="sm"
        aria-label="Language"
        value={locale}
        onChange={(e) => setLocale(e.target.value as SupportedLocale)}
      >
        {SUPPORTED_LOCALES.map((loc) => (
          <option key={loc} value={loc}>{LOCALE_LABEL[loc]}</option>
        ))}
      </Select>
    </div>
  );
}
